import { z } from 'zod';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from './password-rules.js';
/**
 * The sign-up form's rules and messages, written once here so the dashboard
 * shows the same text whether it checks before sending or the API refuses the
 * request (`patterns/error-handling.md`, 3). The copy is the account sign-up
 * feature's `ux.md` and `technical.md`, Input rules.
 *
 * Spaces around the name and the email address are ignored, and a name of only
 * spaces counts as empty. The email address is lowercased, as the login stores
 * it. The password is used exactly as typed: never trimmed
 * or case-folded (`patterns/auth-patterns.md`, 1).
 */
/** The longest name, after trimming, so it is never cut off where it shows. */
export const NAME_MAX_LENGTH = 80;
/** The longest address a mail system must accept. */
export const EMAIL_MAX_LENGTH = 254;
export const SIGNUP_FORM_MESSAGES = {
    nameRequired: 'Enter your name.',
    nameTooLong: `Use ${NAME_MAX_LENGTH} characters or fewer.`,
    emailRequired: 'Enter your email address.',
    emailTooLong: `Use ${EMAIL_MAX_LENGTH} characters or fewer.`,
    emailFormat: 'Enter an email address in the format name@example.com.',
    passwordRequired: 'Enter a password.',
    passwordTooShort: `This password is too short. Use at least ${PASSWORD_MIN_LENGTH} characters.`,
    passwordTooLong: `This password is too long. Use ${PASSWORD_MAX_LENGTH} characters or fewer.`,
    acceptTerms: 'To create an account, agree to the Terms and Conditions and the Privacy Policy.',
};
/** The Terms and Privacy Policy a person agrees to at sign-up, on Groundly's own website. */
export const TERMS_URL = 'https://groundly-ai.com/terms';
export const PRIVACY_URL = 'https://groundly-ai.com/privacy';
/**
 * Which Terms and Privacy Policy sign-up agrees to: their date. Recorded on
 * the account with when it was agreed; change it whenever either is updated.
 */
export const TERMS_VERSION = '2026-10-22';
/** The password hint, shown before anything is typed (WCAG 3.3.2). */
export const PASSWORD_REQUIREMENTS_HINT = `At least ${PASSWORD_MIN_LENGTH} characters.`;
/**
 * Piped rather than chained: each step only runs once the one before it has
 * passed, so an empty field reports only that it is empty.
 */
export const signupEmailSchema = z
    .string()
    .transform((value) => value.trim().toLowerCase())
    .pipe(z.string().min(1, { error: SIGNUP_FORM_MESSAGES.emailRequired }))
    .pipe(z.string().max(EMAIL_MAX_LENGTH, { error: SIGNUP_FORM_MESSAGES.emailTooLong }))
    .pipe(z.email({ error: SIGNUP_FORM_MESSAGES.emailFormat }));
const nameSchema = z
    .string()
    .transform((value) => value.trim())
    .pipe(z.string().min(1, { error: SIGNUP_FORM_MESSAGES.nameRequired }))
    .pipe(z.string().max(NAME_MAX_LENGTH, { error: SIGNUP_FORM_MESSAGES.nameTooLong }));
const passwordSchema = z
    .string()
    .min(1, { error: SIGNUP_FORM_MESSAGES.passwordRequired })
    .pipe(z
    .string()
    .min(PASSWORD_MIN_LENGTH, { error: SIGNUP_FORM_MESSAGES.passwordTooShort })
    .max(PASSWORD_MAX_LENGTH, { error: SIGNUP_FORM_MESSAGES.passwordTooLong }));
export const signupFormSchema = z.object({
    name: nameSchema,
    email: signupEmailSchema,
    password: passwordSchema,
    /** The person agreed to the Terms and the Privacy Policy: sign-up needs it. */
    acceptTerms: z
        .boolean({ error: SIGNUP_FORM_MESSAGES.acceptTerms })
        .refine((accepted) => accepted, { error: SIGNUP_FORM_MESSAGES.acceptTerms }),
});
//# sourceMappingURL=signup-form.js.map